import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  CloudUpload,
  Group,
  ReceiptLong,
  TableChart,
  Download,
  Description,
  Settings,
  ExpandLess,
  ExpandMore,
  FolderOpen,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openConfigs, setOpenConfigs] = React.useState<boolean>(false);
  const [openManageFiles, setOpenManageFiles] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const menuItems = [
    {
      text: t('sidebar.dashboard'),
      icon: <Dashboard />,
      path: '/dashboard',
      roles: ['admin', 'user'],
    },
    {
      text: t('sidebar.upload'),
      icon: <CloudUpload />,
      path: '/upload',
      roles: ['admin', 'user'],
    },
    {
      text: t('sidebar.excelMapping'),
      icon: <TableChart />,
      path: '/excel-mapping',
      roles: ['admin', 'user'],
    },
    {
      text: t('sidebar.exportCsv'),
      icon: <Download />,
      path: '/export-csv',
      roles: ['admin', 'user'],
    },
    {
      text: t('sidebar.templates'),
      icon: <Description />,
      path: '/templates',
      roles: ['admin', 'user'],
    },
    {
      text: t('sidebar.users'),
      icon: <Group />,
      path: '/users',
      roles: ['admin'],
    },
    {
      text: t('sidebar.audit'),
      icon: <ReceiptLong />,
      path: '/audit-logs',
      roles: ['admin'],
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    user?.role && item.roles.includes(user.role)
  );

  const drawerContent = (
    <Box sx={{ width: 250, height: '100%' }}>
      <List sx={{ pt: 2 }}>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path || 
                       (item.path === '/dashboard' && location.pathname === '/')}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Manage Files submenu */}
        {user?.role === 'admin' && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenManageFiles(v => !v)} selected={location.pathname.startsWith('/manage-files/')}>
                <ListItemIcon>
                  <FolderOpen />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.manageFiles')} />
                {openManageFiles ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openManageFiles} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => handleNavigation('/manage-files/dashboard')} selected={location.pathname === '/manage-files/dashboard'}>
                    <ListItemIcon>
                      <Dashboard />
                    </ListItemIcon>
                    <ListItemText primary={t('sidebar.manageFilesDashboard')} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>
        )}

        {/* Configuraciones submenu */}
        {user?.role === 'admin' && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setOpenConfigs(v => !v)} selected={location.pathname.startsWith('/config/')}>
                <ListItemIcon>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary={t('sidebar.settings')} />
                {openConfigs ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={openConfigs} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={() => handleNavigation('/config/environments')} selected={location.pathname === '/config/environments'}>
                    <ListItemText primary={t('sidebar.environments')} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="persistent"
      open={open}
      onClose={onClose}
      sx={{
        width: open ? 250 : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 250,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          borderRight: '1px solid',
          borderColor: 'divider',
          top: 0,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
